import React from 'react';
import { getConfig } from '@cian/config/browser';
import { hydrate } from 'react-dom';
import { getLogger } from '@cian/logger/browser';
import { ErrorLogComponent } from '@cian/error-log-component';
import {
  IDesktopGetNotificationsRequest,
  fetchDesktopGetNotifications,
  TDesktopGetNotificationsResponse,
} from '../../../shared/repositories/notification-center/v1/desktop-get-notifications';
import {
  fetchDesktopGetNotificationsCount,
  TDesktopGetNotificationsCountResponse,
} from '../../../shared/repositories/notification-center/v1/desktop-get-notifications-count';
import { fetchDesktopMarkAsRead } from '../../../shared/repositories/notification-center/v1/desktop-mark-as-read';
import {
  TDesktopMarkAllAsReadResponse,
  fetchDesktopMarkAllAsRead,
} from '../../../shared/repositories/notification-center/v1/desktop-mark-all-as-read';
import {
  fetchDesktopDeleteAllNotifications,
  TDesktopDeleteAllNotificationsResponse,
} from '../../../shared/repositories/notification-center/v1/desktop-delete-all-notifications';
import { getHttpApi } from '@cian/http-api/browser';

export const initializeNotificationCenter = async () => {
  const notificationCenterPanelElement = document.getElementById('header-user-notification-center');
  const notificationCenterMobilePanelElement = document.getElementById('header-user-notification-center--mobile');
  const config = getConfig();
  const subdomain = config.getStrict<string>('subdomain');
  const notificationCenterData = window.__header_notification_center_data__;
  const { isMobileDevice } = notificationCenterData;
  const logger = getLogger();

  if (notificationCenterPanelElement) {
    const { NotificationCenter } = await import('../../../shared/components/NotificationCenter');

    hydrate(
      <ErrorLogComponent logger={logger}>
        <NotificationCenter
          httpApi={getHttpApi()}
          subdomain={subdomain}
          notificationCenterData={notificationCenterData}
          getNotifications={getNotifications}
          getHasNewNotifications={getHasNewNotifications}
          markNotificationAsRead={markNotificationAsRead}
          markAllNotificationAsRead={markAllNotificationAsRead}
          deleteAllNotifications={deleteAllNotifications}
        />
      </ErrorLogComponent>,
      isMobileDevice ? notificationCenterMobilePanelElement : notificationCenterPanelElement,
    );
  }
};

export async function getNotifications(parameters: IDesktopGetNotificationsRequest) {
  const config = { ecnhancersConfig: { timeout: 1000 } };

  return fetchDesktopGetNotifications({ httpApi: getHttpApi(), parameters, config })
    .then((response: TDesktopGetNotificationsResponse) => {
      if (response.statusCode !== 200) {
        throw new Error(response.response.message);
      }

      const {
        response: { notifications },
      } = response;
      if (notifications) {
        return notifications;
      }

      return [];
    })
    .catch(error => {
      getLogger().error(error, {
        domain: 'fetchDesktopGetNotifications',
        details: {
          repository: 'frontend-header',
          service: 'header',
        },
      });

      throw new Error(error);
    });
}

export async function getHasNewNotifications() {
  const config = { ecnhancersConfig: { timeout: 1000 } };

  return fetchDesktopGetNotificationsCount({ httpApi: getHttpApi(), parameters: {}, config })
    .then((response: TDesktopGetNotificationsCountResponse) => {
      if (response.statusCode !== 200) {
        throw new Error(response.response.message);
      }

      const {
        response: { hasUnreadNotifications },
      } = response;
      if (hasUnreadNotifications) {
        return hasUnreadNotifications;
      }

      return false;
    })
    .catch(error => {
      getLogger().error(error, {
        domain: 'fetchDesktopGetNotificationsCount',
        details: {
          repository: 'frontend-header',
          service: 'header',
        },
      });

      throw new Error(error);
    });
}

export async function markNotificationAsRead(id: number) {
  const config = { ecnhancersConfig: { timeout: 1000 } };

  return fetchDesktopMarkAsRead({ httpApi: getHttpApi(), parameters: { ids: [id] }, config })
    .then((response: TDesktopMarkAllAsReadResponse) => {
      if (response.statusCode === 400) {
        throw new Error(response.response.message);
      }

      return id;
    })
    .catch(error => {
      getLogger().error(error, {
        domain: 'fetchDesktopMarkAsRead',
        details: {
          repository: 'frontend-header',
          service: 'header',
        },
      });

      return null;
    });
}

export async function markAllNotificationAsRead() {
  const config = { ecnhancersConfig: { timeout: 1000 } };
  const parameters = {};

  return fetchDesktopMarkAllAsRead({ httpApi: getHttpApi(), parameters, config })
    .then((response: TDesktopMarkAllAsReadResponse) => {
      if (response.statusCode === 400) {
        throw new Error(response.response.message);
      }
    })
    .catch(error => {
      getLogger().error(error, {
        domain: 'fetchDesktopMarkAllAsRead',
        details: {
          repository: 'frontend-header',
          service: 'header',
        },
      });
    });
}

export async function deleteAllNotifications() {
  const config = { ecnhancersConfig: { timeout: 1000 } };
  const parameters = {};

  return fetchDesktopDeleteAllNotifications({ httpApi: getHttpApi(), parameters, config })
    .then((response: TDesktopDeleteAllNotificationsResponse) => {
      if (response.statusCode === 400) {
        throw new Error(response.response.message);
      }
    })
    .catch(error => {
      getLogger().error(error, {
        domain: 'fetchDesktopDeleteAllNotifications',
        details: {
          repository: 'frontend-header',
          service: 'header',
        },
      });
    });
}
